import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";

const CONTROL_POINTS = [
  {
    icon: "ri-ruler-2-line",
    title: "좌우 Roll Gap 균일 제어",
    desc: "양측 실린더 동기 제어로 균일한 갭 유지",
    image: "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/93759a3c1ce27b9e1833643272ad2611.png",
  },
  {
    icon: "ri-arrow-up-down-line",
    title: "상하 실린더 위치 제어",
    desc: "SSI 위치센서 기반 폐루프 제어",
    image: "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/5d7aecbdc9c40d4350a3738f9b826982.png",
  },
  {
    icon: "ri-pulse-line",
    title: "압력센서 기반 하중 제어",
    desc: "하중 / Pressure 피드백 제어",
    image: "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/8fd8babbe7fa3f155bf1f157b15535d9.png",
  },
  {
    icon: "ri-equalizer-line",
    title: "서보밸브 기반 유량·방향 서보 제어",
    desc: "MOOG Series 서보밸브 적용",
    image: "https://storage.helloreaddy.io/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/3f140483-30a2-49e4-8e42-65be9cf140c0_compressed___T__.webp",
  },
  {
    icon: "ri-radar-line",
    title: "위치압력하중센서 기반 폐루프 제어",
    desc: "정밀 닫힘 루프 위치 제어",
    image: "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/7a607afe41919c1226f12e2b9f9f68b1.png",
  },
  {
    icon: "ri-database-2-line",
    title: "유압유니트 및 매니폴드 구성",
    desc: "HPU·Manifold·Cylinder 통합 설계",
    image: "https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/692c8adca408c5bc48da06a1453eccb7.png",
  },

];

const SUPPLY_SCOPE = [
  { group: "Hardware", icon: "ri-database-2-line", items: ["Hydraulic Unit + piping work", "Servo Valve", "Servo Cylinder", "Pressure Sensor", "Position Sensor", "Motion Controller + programming"] },
  { group: "Engineering", icon: "ri-draft-line", items: ["Electrical Design", "Hydraulic Circuit Review", "Program & Parameter Setting", "Installation & Commissioning"] },
];


export default function RollPressPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Roll Press Solution"
        title="Roll Press Servo Hydraulic Control"
        subtitle="Roll Press 장비의 정밀 압력·위치·갭 제어를 위한 서보유압 및 모션제어 통합 솔루션"
        crumbs={[{ label: "HOME", to: "/" }, { label: "Roll Press" }]}
        imageUrl="https://readdy.ai/api/search-image?query=Industrial%20heavy%20duty%20roll%20press%20line%20with%20twin%20steel%20rollers%20and%20hydraulic%20cylinders%20in%20a%20clean%20modern%20factory%2C%20detailed%20mechanical%20textures%2C%20cool%20industrial%20lighting%2C%20editorial%20B2B%20engineering%20photography%2C%20muted%20steel%20gray%20and%20cool%20blue%20palette&width=1920&height=900&seq=mctech-rollpress-hero-01&orientation=landscape"
      />

      {/* Intro */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <p className="text-base md:text-lg text-foreground-800 leading-relaxed text-center">
            Roll Press 장비는 롤러 사이의{" "}
            <span className="font-bold text-primary-700">간격, 압력, 실린더 위치, 좌우 균형</span>{" "}
            제어가 중요합니다. MC TECH는 롤프레스 장비의 정밀 제어를 위해{" "}
            <span className="font-bold text-accent-700">서보유압 시스템</span>과{" "}
            <span className="font-bold text-accent-700">모션제어 시스템</span>을 통합하여 제공합니다.
          </p>
        </div>
      </section>

      {/* Control Points */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-10 md:mb-14 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              Control Points
              <span className="w-6 h-px bg-accent-500" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
              주요 제어 포인트
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CONTROL_POINTS.map((c, i) => (
              <div
                key={c.title}
                className="bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 transition-colors"
              >
                {c.image ? (
                  <div className="w-full bg-background-100 flex items-center justify-center" style={{aspectRatio: '4/3'}}>
                    <img
                      src={c.image}
                      alt={c.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full bg-background-100 flex items-center justify-center" style={{aspectRatio: '4/3'}}>
                    <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                      <i className={`${c.icon} text-3xl w-8 h-8 flex items-center justify-center`} />
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-heading font-bold text-foreground-950">
                      {c.title}
                    </h3>
                    <span className="text-xs font-heading font-bold text-secondary-500 tracking-wider">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supply Scope */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              MC TECH Supply Scope
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 leading-tight">
              MC TECH가{" "}
              <span className="text-primary-700">공급하는 범위</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              하드웨어 공급부터 회로 검토, 제어 프로그램, 파라미터 설정,
              통합설계, 현장 시운전까지 일관되게 책임집니다.
            </p>
            <div className="mt-6 rounded-xl overflow-hidden border border-background-200 h-64">
              <img
                src="https://readdy.ai/api/search-image?query=Industrial%20hydraulic%20unit%20HPU%20with%20servo%20valve%20stack%20and%20control%20cabinet%20in%20a%20clean%20modern%20factory%2C%20cool%20neutral%20industrial%20lighting%2C%20editorial%20industrial%20B2B%20photography%2C%20muted%20steel%20gray%20palette%2C%20detailed%20mechanical%20textures&width=1200&height=900&seq=mctech-rollpress-supply-02&orientation=landscape"
                alt="MC TECH Supply Scope"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            {SUPPLY_SCOPE.map((g) => (
              <div
                key={g.group}
                className="bg-background-100 border border-background-200 rounded-xl p-6 md:p-7"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                      <i className={`${g.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                    </div>
                    <span className="text-base font-heading font-bold text-foreground-950">
                      {g.group}
                    </span>
                  </div>

                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {g.items.map((it) => (
                    <div
                      key={it}
                      className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-3 py-2.5"
                    >
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600" />
                      <span className="text-sm font-medium text-foreground-900">
                        {it}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
}